"use client";

import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Shared motion engine for content pages (/portfolio, /blog, /contato).
 *
 *  - hero text stagger (selector configurable via the `heroSelector` prop)
 *  - reveals (.reveal / .reveal-tilt / .reveal-fade)
 *  - magnetic CTAs (.btn-magnetic)
 *  - 3D tilt + spotlight on cards (selector configurable)
 *  - parallax of cosmos nebulas
 *  - scroll progress bar
 *  - prefers-reduced-motion: skip animations + still expose console panels
 */
export function ContentMotion({
  heroSelector = ".content-hero",
  tiltSelector = ".case, .blog-card",
}: { heroSelector?: string; tiltSelector?: string } = {}) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document.querySelectorAll<HTMLElement>(".console--content").forEach((el) => {
        el.style.opacity = "1";
      });
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Hero intro
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" }, delay: 0.15 });

      if (document.querySelector(`${heroSelector} .crumb`)) {
        heroTl.from(`${heroSelector} .crumb`, { opacity: 0, y: 8, duration: 0.5 });
      }
      if (document.querySelector(`${heroSelector} .hero__telemetry`)) {
        heroTl.from(`${heroSelector} .hero__telemetry`, { opacity: 0, y: 10, duration: 0.7 }, "-=0.2");
      }
      if (document.querySelectorAll(`${heroSelector} .hero__title .char`).length > 0) {
        heroTl.from(`${heroSelector} .hero__title .char`, { yPercent: 110, opacity: 0, duration: 0.7, stagger: 0.012, ease: "power3.out" }, "-=0.4");
      }
      if (document.querySelector(`${heroSelector} .hero__sub`)) {
        heroTl.from(`${heroSelector} .hero__sub`, { opacity: 0, y: 10, duration: 0.6 }, "-=0.3");
      }
      if (document.querySelector(`${heroSelector} .hero__ctas`)) {
        heroTl.from(`${heroSelector} .hero__ctas`, { opacity: 0, y: 10, duration: 0.6 }, "-=0.4");
      }

      // Optional console panel (used by /portfolio hero)
      if (document.querySelector(".console--content")) {
        gsap.from(".console--content", { opacity: 0, x: 30, duration: 1, delay: 0.8, ease: "power3.out" });
      }
      if (document.querySelectorAll(".console--content .console__row").length > 0) {
        gsap.from(".console--content .console__row", { opacity: 0, x: 30, duration: 0.5, stagger: 0.1, ease: "power2.out", delay: 1.0 });
      }

      // Reveals
      gsap.utils.toArray<HTMLElement>(".reveal").forEach((el) => {
        gsap.from(el, {
          opacity: 0, y: 40, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
        });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-tilt").forEach((el) => {
        gsap.from(el, {
          opacity: 0, y: 60, rotateX: -12, duration: 1, ease: "power3.out", transformOrigin: "center top",
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
        });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-fade").forEach((el) => {
        gsap.from(el, {
          opacity: 0, duration: 1.2, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none none" },
        });
      });

      // Nebula parallax
      gsap.to(".cosmos__nebula--a", { y: 200, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--b", { y: -260, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--c", { y: 180, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });

      // Card tilt + spotlight
      if (tiltSelector) {
        document.querySelectorAll<HTMLElement>(tiltSelector).forEach((card) => {
          const onMove = (e: MouseEvent) => {
            const r = card.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width;
            const y = (e.clientY - r.top) / r.height;
            card.style.setProperty("--mx", x * 100 + "%");
            card.style.setProperty("--my", y * 100 + "%");
            gsap.to(card, { rotateX: (0.5 - y) * 6, rotateY: (x - 0.5) * 8, duration: 0.4, ease: "power2.out" });
          };
          const onLeave = () => gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.6, ease: "power3.out" });
          card.addEventListener("mousemove", onMove);
          card.addEventListener("mouseleave", onLeave);
        });
      }

      // Magnetic CTAs
      document.querySelectorAll<HTMLElement>(".btn-magnetic").forEach((btn) => {
        const onMove = (e: MouseEvent) => {
          const r = btn.getBoundingClientRect();
          const x = e.clientX - r.left - r.width / 2;
          const y = e.clientY - r.top - r.height / 2;
          gsap.to(btn, { x: x * 0.18, y: y * 0.3, duration: 0.4, ease: "power2.out" });
        };
        const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
        btn.addEventListener("mousemove", onMove);
        btn.addEventListener("mouseleave", onLeave);
      });

      // Scroll progress
      const progress = document.querySelector<HTMLElement>(".scroll-progress");
      if (progress) {
        ScrollTrigger.create({
          start: 0,
          end: "max",
          onUpdate: (self) => { progress.style.width = self.progress * 100 + "%"; },
        });
      }

      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => ctx.revert();
  }, [heroSelector, tiltSelector]);

  return null;
}
